import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { onApiMutationError, type ApiMutationError } from '../lib/api';

/**
 * 🔴 "NADA FALLA EN SILENCIO" (regla 2026-09-13).
 *
 * Escucha el bus global de errores de MUTACIÓN de `api.ts` y muestra un toast
 * claro y accionable — JAMÁS `window.alert` (bloquea la automatización y se ve de
 * prototipo). Se monta UNA vez en `main.tsx`, así cubre TODO: el POS, la tienda
 * pública, el lock de piso, etc. Portal a `document.body` (regla de modales).
 *
 * Origen: el 500 de `/fire` no se veía en pantalla; Arturo creía que la comanda se
 * había enviado. Ahora cualquier POST/PUT/PATCH/DELETE 4xx/5xx aterriza aquí.
 *
 * Anti-spam: una ráfaga de errores idénticos (p.ej. 10× el mismo 500) colapsa en
 * UN toast con contador, no diez.
 */
interface Toast { id: number; message: string; status: number; count: number; at: number }

const AUTO_DISMISS_MS = 7000;
const DEDUP_WINDOW_MS = 5000;
let seq = 0;

export function ToastHost() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const off = onApiMutationError((e: ApiMutationError) => {
      setToasts((prev) => {
        const now = Date.now();
        // Colapsa ráfagas idénticas recientes en un solo toast con contador.
        const dupe = prev.find((t) => t.message === e.message && now - t.at < DEDUP_WINDOW_MS);
        if (dupe) {
          return prev.map((t) => (t.id === dupe.id ? { ...t, count: t.count + 1, at: now } : t));
        }
        return [...prev, { id: ++seq, message: e.message, status: e.status, count: 1, at: now }];
      });
    });
    return () => { off(); };
  }, []);

  // Auto-descarte: cada toast se va solo a los AUTO_DISMISS_MS de su última vez.
  useEffect(() => {
    if (!toasts.length) return;
    const iv = window.setInterval(() => {
      const now = Date.now();
      setToasts((prev) => prev.filter((t) => now - t.at < AUTO_DISMISS_MS));
    }, 1000);
    return () => window.clearInterval(iv);
  }, [toasts.length]);

  const dismiss = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  if (!toasts.length) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[9999] flex flex-col items-center gap-2 px-4"
      aria-live="assertive"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role="alert"
          className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-rose-200 bg-white px-4 py-3 shadow-lg ring-1 ring-rose-100"
        >
          <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full bg-rose-100 text-xs font-bold text-rose-700">!</span>
          <div className="min-w-0 flex-1 text-sm text-slate-700">
            {t.message}
            {t.count > 1 && <span className="ml-1 text-xs text-slate-400">(×{t.count})</span>}
          </div>
          <button
            onClick={() => dismiss(t.id)}
            className="flex-none rounded-md px-1.5 text-slate-400 hover:text-slate-700"
            aria-label="Cerrar aviso"
          >
            ✕
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
